'use client';
import { memo, Suspense, type ReactNode, useState, useEffect, useRef } from 'react';

interface DeferredSectionProps {
  children: ReactNode;
  fallback?: ReactNode;
  rootMargin?: string;
  eager?: boolean;
  className?: string;
}

const DeferredSection = memo(({
  children,
  fallback,
  rootMargin = '250px',
  eager = false,
  className,
}: DeferredSectionProps) => {
  const [isVisible, setIsVisible] = useState(eager);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (eager || isVisible) return;

    const element = containerRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin, threshold: 0 }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [rootMargin, eager, isVisible]);

  return (
    <div
      ref={containerRef}
      className={className}
      style={!isVisible ? { contentVisibility: 'auto', containIntrinsicSize: '0 500px' } : { contentVisibility: 'visible' }}
    >
      {isVisible ? <Suspense fallback={fallback || null}>{children}</Suspense> : (fallback || null)}
    </div>
  );
});

DeferredSection.displayName = 'DeferredSection';

export default DeferredSection;
